/**
 * /auto-captions — Edit-by-transcript (word-level video cut)
 * ---------------------------------------------------------------
 * Descript-style: delete a word in the transcript editor, the corresponding
 * audio+video range is physically REMOVED from the burned MP4 export.
 *
 * What this module is and isn't:
 *   IS:  a thin state + UI + FFmpeg-filter-string helper. Pure browser, no
 *        worker spawn, no server processing. Shares the global FFmpegProcessor
 *        only at burn time (via the patch in auto-captions-pipeline.js).
 *   IS NOT: the burn pipeline. The actual ffmpeg invocation lives in
 *           __acBurnExport — this module only contributes the select/aselect
 *           filter string + the remapped words array for ASS regeneration.
 *
 * Public API on window.__acEditByTranscript:
 *   .deleteWord(idx)             → mark state.words[idx]._deleted = true, re-render editor + chip
 *   .restoreWord(idx)            → unset _deleted, re-render
 *   .reset()                     → clear every _deleted flag
 *   .getCutList()                → sorted [{start, end}] ranges to REMOVE, adjacent merged
 *   .getKeepList(videoDuration)  → inverse: sorted [{start, end}] ranges to KEEP +
 *                                  { ranges, totalDuration, hasCuts }
 *   .buildFFmpegSelectFilter(keepList, videoDuration)
 *                                → { video, audio } filter strings ready for ffmpeg's
 *                                  -vf / -af positions; video uses select+setpts,
 *                                  audio uses aselect+asetpts. Returns null when no cuts.
 *   .mapWordsToCutTimeline(words)→ produces a NEW words array with start/end shifted
 *                                  onto the cut-down timeline; used to rebuild the ASS
 *                                  subtitle file for the shorter exported video.
 *   .hasCuts()                   → boolean shortcut
 *   .installEditorHooks()        → idempotently wires Backspace/Delete keyboard on
 *                                  .ac-ed-word, plus the footer chip above export
 *
 * Coordinate system:
 *   Word objects use { start, end } in SECONDS, matching the rest of
 *   the auto-captions pipeline. The select/aselect 'between(t,...)' filter
 *   takes the same unit so values pass through without conversion.
 *
 * Merge tolerance:
 *   Adjacent _deleted words are merged into a single cut range when the gap
 *   between them is <= MERGE_EPSILON seconds. Without this, FFmpeg's select
 *   filter accumulates per-word between() clauses which (a) explodes the filter
 *   string for long deletions, (b) leaves frame-perfect micro-gaps that cause
 *   visible audio pops. 0.05s ≈ 1 frame at 24fps is well under the perceptible
 *   click threshold.
 */
(function(){
    'use strict';

    var MERGE_EPSILON = 0.05; // seconds — gap below this merges adjacent cuts

    /* --------- internal helpers --------- */

    /* Словарь приезжает из блейда (`window.__acEbtI18n`): этот файл статический,
       blade его не компилирует. Английский остаётся ЗАПАСНЫМ — если словарь не
       доехал, подпись на кнопке лучше пустой кнопки. */
    function EBT(key, fallback){
        try { var d = window.__acEbtI18n || {}; return d[key] || fallback; }
        catch (_) { return fallback; }
    }

    function getState(){
        return window.__acState || null;
    }

    function getWords(){
        var st = getState();
        return (st && Array.isArray(st.words)) ? st.words : [];
    }

    /* Walk state.words once, build a list of [from, to] cut ranges merging
       adjacent _deleted words. Returns ranges in ascending start order. */
    function collectCuts(){
        var words = getWords();
        var cuts = [];
        var current = null;
        for (var i = 0; i < words.length; i++){
            var w = words[i];
            if (!w || !w._deleted) {
                if (current) { cuts.push(current); current = null; }
                continue;
            }
            var s = typeof w.start === 'number' ? w.start : 0;
            var e = typeof w.end === 'number' ? w.end : s;
            if (e < s) { var tmp = s; s = e; e = tmp; }
            if (current && s - current.end <= MERGE_EPSILON) {
                current.end = Math.max(current.end, e);
            } else {
                if (current) cuts.push(current);
                current = { start: s, end: e };
            }
        }
        if (current) cuts.push(current);
        /* Defensive: collapse any pathological overlaps from out-of-order words. */
        cuts.sort(function(a, b){ return a.start - b.start; });
        var out = [];
        for (var j = 0; j < cuts.length; j++){
            if (out.length && cuts[j].start <= out[out.length - 1].end + MERGE_EPSILON) {
                out[out.length - 1].end = Math.max(out[out.length - 1].end, cuts[j].end);
            } else {
                out.push(cuts[j]);
            }
        }
        return out;
    }

    /* Invert a cut list into keep ranges over [0, totalDuration]. */
    function computeKeepList(videoDuration){
        var dur = (typeof videoDuration === 'number' && isFinite(videoDuration) && videoDuration > 0)
            ? videoDuration
            : (function(){ var st = getState(); return (st && st.duration) || 0; })();
        var cuts = collectCuts();
        if (!cuts.length) {
            return {
                ranges: dur > 0 ? [{ start: 0, end: dur }] : [],
                totalDuration: dur,
                hasCuts: false,
                cutTotal: 0,
            };
        }
        var ranges = [];
        var cursor = 0;
        var cutTotal = 0;
        for (var i = 0; i < cuts.length; i++){
            var c = cuts[i];
            cutTotal += Math.max(0, c.end - c.start);
            if (c.start > cursor + 1e-6) {
                ranges.push({ start: cursor, end: Math.min(c.start, dur || c.start) });
            }
            cursor = Math.max(cursor, c.end);
        }
        if (dur > 0 && cursor < dur - 1e-6) {
            ranges.push({ start: cursor, end: dur });
        }
        var keptTotal = 0;
        for (var r = 0; r < ranges.length; r++) keptTotal += ranges[r].end - ranges[r].start;
        return {
            ranges: ranges,
            totalDuration: keptTotal,
            hasCuts: true,
            cutTotal: cutTotal,
        };
    }

    /* Render select / aselect filter strings.
       Uses FFmpeg's between(t,A,B) expression OR'd across keep ranges; setpts
       re-bases timestamps to the cut timeline so the muxer emits a contiguous
       file. The string is ready to drop into -vf / -af (or extraFilters /
       extraAudioFilters on the exporter). */
    function buildFilterStrings(keepList, videoDuration){
        if (!keepList || !keepList.ranges || !keepList.ranges.length || !keepList.hasCuts) {
            return null;
        }
        var parts = [];
        for (var i = 0; i < keepList.ranges.length; i++){
            var r = keepList.ranges[i];
            parts.push('between(t,' + r.start.toFixed(3) + ',' + r.end.toFixed(3) + ')');
        }
        var expr = parts.join('+');
        return {
            video: "select='" + expr + "',setpts=N/FRAME_RATE/TB",
            audio: "aselect='" + expr + "',asetpts=N/SR/TB",
            expr:  expr,
        };
    }

    /* Remap a words array onto the cut-down timeline so ASS can be rebuilt
       against the shorter video. Deleted words are dropped; surviving words
       have their start/end shifted by the cumulative cut duration that
       precedes them. Words straddling a cut edge (rare — would mean a partial
       deletion at sub-word level) are clamped. */
    function remapToCutTimeline(words){
        if (!Array.isArray(words) || !words.length) return [];
        var cuts = collectCuts();
        if (!cuts.length) return words.slice();
        var out = [];
        for (var i = 0; i < words.length; i++){
            var w = words[i];
            if (!w || w._deleted) continue;
            var s = typeof w.start === 'number' ? w.start : 0;
            var e = typeof w.end === 'number' ? w.end : s;
            /* Sum cut duration falling before this word's start. */
            var shiftStart = 0, shiftEnd = 0;
            for (var c = 0; c < cuts.length; c++){
                var ct = cuts[c];
                if (ct.end <= s) shiftStart += (ct.end - ct.start);
                if (ct.end <= e) shiftEnd   += (ct.end - ct.start);
            }
            var clone = {};
            for (var k in w) if (Object.prototype.hasOwnProperty.call(w, k)) clone[k] = w[k];
            clone.start = Math.max(0, s - shiftStart);
            clone.end   = Math.max(clone.start, e - shiftEnd);
            out.push(clone);
        }
        return out;
    }

    /* --------- UI: editor word hooks + cut chip --------- */

    var hooksInstalled = false;
    var chipEl = null;

    function ensureChip(){
        if (chipEl && document.body.contains(chipEl)) return chipEl;
        var burnBtn = document.getElementById('ac-export-burn');
        if (!burnBtn || !burnBtn.parentNode) return null;
        chipEl = document.createElement('div');
        chipEl.id = 'ac-ebt-chip';
        chipEl.className = 'ac-ebt-chip';
        chipEl.setAttribute('role', 'status');
        chipEl.setAttribute('aria-live', 'polite');
        chipEl.style.cssText = [
            'display:none',
            'margin:8px 0',
            'padding:8px 12px',
            'border-radius:8px',
            'background:rgba(255,193,7,0.12)',
            'border:1px solid rgba(255,193,7,0.5)',
            'color:var(--text-primary,#fff)',
            'font-size:13px',
            'gap:10px',
            'align-items:center',
            'justify-content:space-between',
        ].join(';');
        var label = document.createElement('span');
        label.className = 'ac-ebt-chip-label';
        var undo = document.createElement('button');
        undo.type = 'button';
        undo.textContent = EBT('undoAll', 'Undo all');
        undo.className = 'ac-ebt-chip-undo';
        undo.setAttribute('aria-label', EBT('undoAllAria', 'Restore all deleted words'));
        undo.style.cssText = 'background:transparent;border:1px solid currentColor;color:inherit;border-radius:6px;padding:4px 10px;cursor:pointer;font-size:12px';
        undo.addEventListener('click', function(){
            api.reset();
        });
        chipEl.appendChild(label);
        chipEl.appendChild(undo);
        burnBtn.parentNode.insertBefore(chipEl, burnBtn);
        return chipEl;
    }

    function fmtSecs(s){
        if (!isFinite(s) || s < 0) s = 0;
        if (s < 10) return s.toFixed(1) + 's';
        var m = Math.floor(s / 60), r = Math.round(s - m * 60);
        if (m < 1) return Math.round(s) + 's';
        return m + 'm ' + r + 's';
    }

    function renderChip(){
        var el = ensureChip();
        if (!el) return;
        var cuts = collectCuts();
        if (!cuts.length) {
            el.style.display = 'none';
            return;
        }
        var words = getWords();
        var deletedCount = 0;
        for (var i = 0; i < words.length; i++) if (words[i] && words[i]._deleted) deletedCount++;
        var total = 0;
        for (var c = 0; c < cuts.length; c++) total += cuts[c].end - cuts[c].start;
        el.style.display = 'flex';
        var label = el.querySelector('.ac-ebt-chip-label');
        if (label) {
            /* ⚠️ Английское множественное число («word» / «words») выводилось
               прямо здесь — в русском и арабском форм больше двух, и такой
               способ там неверен в принципе. Число подставляется в готовую
               фразу локали. */
            label.textContent = EBT('chipRemoved', ':n words removed, :dur of video cut')
                .replace(':n', deletedCount)
                .replace(':dur', fmtSecs(total));
        }
    }

    /* Apply visual styling + a11y attrs to the word-bearing spans in the
       editor. The editor itself emits per-word spans only when conf/emph
       markers fire; when neither does, words are plain text nodes. We
       upgrade plain text to spans so deletion works regardless. */
    function decorateEditorWords(){
        var editor = document.getElementById('ac-editor');
        if (!editor) return;
        var lines = editor.querySelectorAll('.ac-ed-line');
        var state = getState();
        if (!state || !Array.isArray(state.lines)) return;
        var globalIdx = 0;
        for (var i = 0; i < lines.length; i++){
            var lineEl = lines[i];
            var textEl = lineEl.querySelector('.ac-ed-text');
            if (!textEl) continue;
            var lineModel = state.lines[i];
            if (!lineModel || !lineModel.words) continue;
            /* Find the first global word index for this line by identity. */
            var startGlobal = state.words.indexOf(lineModel.words[0]);
            if (startGlobal < 0) startGlobal = globalIdx;
            for (var w = 0; w < lineModel.words.length; w++){
                var word = lineModel.words[w];
                var gi = startGlobal + w;
                /* Mark every existing span / text node that corresponds to this word. */
                /* We do not rewrite editor HTML (would fight rebuildLines) — instead we
                   annotate top-level child spans by position. */
            }
            globalIdx = startGlobal + lineModel.words.length;
        }
        /* Simpler + robust: replace each .ac-ed-text content with our own spans on
           every render. We listen for editor changes via MutationObserver below. */
        repaintLine(editor, state);
    }

    /* For each .ac-ed-text, walk its child nodes and rebuild as .ac-ed-word
       spans keyed by global index. Skips speaker chips and preserves
       contenteditable on the parent. */
    function repaintLine(editor, state){
        var lines = editor.querySelectorAll('.ac-ed-line');
        for (var i = 0; i < lines.length; i++){
            var textEl = lines[i].querySelector('.ac-ed-text');
            if (!textEl) continue;
            if (textEl.getAttribute('data-ebt-painted') === '1' && !textEl.getAttribute('data-ebt-dirty')) continue;
            var lineModel = state.lines[i];
            if (!lineModel || !lineModel.words || !lineModel.words.length) continue;
            var startGlobal = state.words.indexOf(lineModel.words[0]);
            if (startGlobal < 0) continue;
            /* Preserve any leading speaker chip. */
            var speakerHtml = '';
            var firstChild = textEl.firstChild;
            if (firstChild && firstChild.nodeType === 1 && firstChild.classList && firstChild.classList.contains('ac-ed-speaker')) {
                speakerHtml = firstChild.outerHTML;
            }
            var parts = [speakerHtml];
            for (var w = 0; w < lineModel.words.length; w++){
                var word = lineModel.words[w];
                var gi = startGlobal + w;
                var classes = 'ac-ed-word';
                if (word._deleted) classes += ' is-deleted';
                var conf = (typeof word.confidence === 'number') ? word.confidence : 1;
                var attrs = ' class="' + classes + '"' +
                    ' data-word-idx="' + gi + '"' +
                    ' tabindex="0"' +
                    ' role="button"' +
                    ' aria-label="' + escapeAttr(word.text || '') +
                    (word._deleted ? ' (deleted, press Enter to restore)' : ' (press Backspace to delete)') +
                    '"';
                if (conf < 0.45) attrs += ' data-conf="low"';
                else if (conf < 0.75) attrs += ' data-conf="med"';
                if (word.emphasis === 'high' || word.emphasis === 'med') attrs += ' data-emph="' + word.emphasis + '"';
                if (word._deleted) attrs += ' aria-pressed="true"';
                parts.push('<span' + attrs + '>' + escapeHtml(word.text || '') + '</span>');
                if (w < lineModel.words.length - 1) parts.push(' ');
            }
            textEl.innerHTML = parts.join('');
            textEl.setAttribute('data-ebt-painted', '1');
            textEl.removeAttribute('data-ebt-dirty');
        }
    }

    function escapeHtml(s){
        return String(s).replace(/[&<>"']/g, function(c){
            return ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' })[c];
        });
    }
    function escapeAttr(s){ return escapeHtml(s); }

    function onEditorKeydown(e){
        var target = e.target;
        if (!target || !target.classList) return;
        if (!target.classList.contains('ac-ed-word')) return;
        var idx = parseInt(target.getAttribute('data-word-idx'), 10);
        if (isNaN(idx)) return;
        var st = getState();
        if (!st || !st.words[idx]) return;

        if (e.key === 'Backspace' || e.key === 'Delete') {
            e.preventDefault();
            e.stopPropagation();
            if (st.words[idx]._deleted) {
                api.restoreWord(idx);
            } else {
                api.deleteWord(idx);
            }
            /* Restore focus to the same word so the user can keep
               punching Backspace to delete a run quickly. */
            setTimeout(function(){
                var next = document.querySelector('.ac-ed-word[data-word-idx="' + idx + '"]');
                if (next) try { next.focus({ preventScroll: true }); } catch(_) { next.focus(); }
            }, 0);
        } else if (e.key === 'Enter') {
            if (st.words[idx]._deleted) {
                e.preventDefault();
                api.restoreWord(idx);
            }
        }
    }

    function onEditorClick(e){
        /* Click on a deleted word toggles restore — friendlier than keyboard-only. */
        var target = e.target;
        if (!target || !target.classList || !target.classList.contains('ac-ed-word')) return;
        if (!target.classList.contains('is-deleted')) return;
        var idx = parseInt(target.getAttribute('data-word-idx'), 10);
        if (isNaN(idx)) return;
        e.preventDefault();
        e.stopPropagation();
        api.restoreWord(idx);
    }

    function injectStyles(){
        if (document.getElementById('ac-ebt-styles')) return;
        var style = document.createElement('style');
        style.id = 'ac-ebt-styles';
        style.textContent = [
            '.ac-ed-word{outline:none;border-radius:3px;padding:0 1px;transition:background .15s,opacity .15s}',
            '.ac-ed-word:focus{background:rgba(99,179,237,0.18);box-shadow:0 0 0 1px rgba(99,179,237,0.6)}',
            '.ac-ed-word.is-deleted{text-decoration:line-through;opacity:.4;cursor:pointer}',
            '.ac-ed-word.is-deleted:focus{opacity:.6;background:rgba(255,107,107,0.18);box-shadow:0 0 0 1px rgba(255,107,107,0.6)}',
            '.ac-ebt-chip{display:flex}',
            '.ac-ebt-chip-undo:hover{background:rgba(255,255,255,0.08)}',
        ].join('\n');
        document.head.appendChild(style);
    }

    function watchEditor(){
        var editor = document.getElementById('ac-editor');
        if (!editor) return;
        /* Repaint on every editor mutation that swaps children (rebuildLines). */
        var mo = new MutationObserver(function(){
            decorateEditorWords();
        });
        mo.observe(editor, { childList: true, subtree: false });
        /* Initial paint */
        decorateEditorWords();
    }

    /* --------- public API --------- */

    var api = {
        deleteWord: function(idx){
            var st = getState();
            if (!st || !st.words || !st.words[idx]) return false;
            if (st.words[idx]._deleted) return false;
            st.words[idx]._deleted = true;
            renderChip();
            /* Repaint editor — flag is per-word, line model already holds the word by reference. */
            var editor = document.getElementById('ac-editor');
            if (editor) {
                /* Force repaint by clearing paint marker on every line. */
                editor.querySelectorAll('.ac-ed-text').forEach(function(t){ t.setAttribute('data-ebt-dirty', '1'); });
                decorateEditorWords();
            }
            if (typeof window.__aeFeature === 'function') {
                try { window.__aeFeature('ebt_word_deleted', { idx: idx }); } catch(_){}
            }
            return true;
        },
        restoreWord: function(idx){
            var st = getState();
            if (!st || !st.words || !st.words[idx]) return false;
            if (!st.words[idx]._deleted) return false;
            delete st.words[idx]._deleted;
            renderChip();
            var editor = document.getElementById('ac-editor');
            if (editor) {
                editor.querySelectorAll('.ac-ed-text').forEach(function(t){ t.setAttribute('data-ebt-dirty', '1'); });
                decorateEditorWords();
            }
            if (typeof window.__aeFeature === 'function') {
                try { window.__aeFeature('ebt_word_restored', { idx: idx }); } catch(_){}
            }
            return true;
        },
        reset: function(){
            var st = getState();
            if (!st || !st.words) return;
            var changed = false;
            for (var i = 0; i < st.words.length; i++){
                if (st.words[i] && st.words[i]._deleted) { delete st.words[i]._deleted; changed = true; }
            }
            renderChip();
            if (changed) {
                var editor = document.getElementById('ac-editor');
                if (editor) {
                    editor.querySelectorAll('.ac-ed-text').forEach(function(t){ t.setAttribute('data-ebt-dirty', '1'); });
                    decorateEditorWords();
                }
                if (typeof window.__aeFeature === 'function') {
                    try { window.__aeFeature('ebt_reset', {}); } catch(_){}
                }
            }
        },
        getCutList: function(){ return collectCuts(); },
        getKeepList: function(videoDuration){ return computeKeepList(videoDuration); },
        buildFFmpegSelectFilter: function(keepList, videoDuration){
            if (!keepList) keepList = computeKeepList(videoDuration);
            return buildFilterStrings(keepList, videoDuration);
        },
        mapWordsToCutTimeline: function(words){
            return remapToCutTimeline(words || getWords());
        },
        hasCuts: function(){
            var words = getWords();
            for (var i = 0; i < words.length; i++) if (words[i] && words[i]._deleted) return true;
            return false;
        },
        /* Idempotent — safe to call multiple times. Wires keyboard, click,
           and the editor MutationObserver. */
        installEditorHooks: function(){
            if (hooksInstalled) return;
            hooksInstalled = true;
            injectStyles();
            var editor = document.getElementById('ac-editor');
            if (editor) {
                editor.addEventListener('keydown', onEditorKeydown, true);
                editor.addEventListener('click', onEditorClick, true);
                watchEditor();
            } else {
                /* Editor may not be in DOM yet (defer). Poll briefly. */
                var tries = 0;
                var poll = setInterval(function(){
                    var e = document.getElementById('ac-editor');
                    if (e) {
                        clearInterval(poll);
                        e.addEventListener('keydown', onEditorKeydown, true);
                        e.addEventListener('click', onEditorClick, true);
                        watchEditor();
                    } else if (++tries > 40) {
                        clearInterval(poll);
                    }
                }, 200);
            }
        },
        /* Internal — exposed for tests + the burn pipeline patch. */
        _internals: {
            MERGE_EPSILON: MERGE_EPSILON,
            collectCuts: collectCuts,
            computeKeepList: computeKeepList,
        },
    };

    window.__acEditByTranscript = api;

    /* Auto-install once the DOM is ready. Safe in both defer + late-script
       loading orders. */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function(){ api.installEditorHooks(); }, { once: true });
    } else {
        api.installEditorHooks();
    }
})();
